// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import React, { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import {
  validateMultipleSelectField,
  validateSelectField,
  validateStringField,
} from 'util/validator';
import { InputDataListField, Suggestion } from '../InputDataListField';
import { SelectField } from '../SelectField';
import styles from './CreateUpdateRouteForm.module.scss';
import { ModalType } from 'util/Enums';
import arrayMutators from 'final-form-arrays';
import { Label } from 'components/atoms/Label';
import { CheckBoxCardV2 } from '../CheckBoxCardV2';
import SelectCollectionMaterialCard from '../SelectCollectionMaterialCard';
import {
  DriverItemType,
  MaterialNameItemType,
  RouteCreateEditModalType,
  RouteForm,
  RouteFormUpdate,
} from 'types/routeManagementTypes';
import { Option } from 'util/Types';
import {
  getCollectRouteDriver,
  getCollectRouteMaterialNames,
} from 'apis/collect_route';
import { ErrorText } from 'components/atoms/ErrorText';
import Popover from 'components/atoms/Popover';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChangeValueType = (field: string, value: any) => void;

interface Props {
  initValues: RouteForm;
  createUpdateModal: RouteCreateEditModalType;
  collectionSiteOptions: Option[];
  isAdminSite?: boolean;
  routeCategories: Suggestion[];
  onSubmit: (routeForm: RouteForm | RouteFormUpdate, routeId?: number) => void;
  onClose: () => void;
}

const CreateUpdateRouteForm: React.FC<Props> = ({
  initValues,
  createUpdateModal,
  collectionSiteOptions,
  isAdminSite,
  routeCategories,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const { routeEditing } = createUpdateModal;

  const [drivers, setDrivers] = useState<DriverItemType[]>([]);
  const [materials, setMaterials] = useState<MaterialNameItemType[]>([]);
  const [initialDriverIds, setInitialDriverIds] = useState<string[]>([]);
  const [initialMaterials, setInitialMaterials] = useState<string[]>([]);

  const handleChangeDriver = (
    id: string,
    selectedDriverIds: string[],
    onChange: (value: string[]) => void
  ) => {
    let resultDriverIds: string[] = [];
    if (selectedDriverIds.includes(id)) {
      resultDriverIds = selectedDriverIds.filter((i) => i !== id);
    } else {
      resultDriverIds = [...selectedDriverIds, id];
    }

    onChange(resultDriverIds);
  };

  const getCollectDriver = async (id: string) => {
    if (!id) {
      setDrivers([]);
      return;
    }

    await getCollectRouteDriver({
      id,
      successCallback: (data) => {
        setDrivers(data.drivers);
      },
      failedCallback: () => {
        setDrivers([]);
      },
    });
  };

  const handleChangeMaterial = (
    id: string,
    selectedMaterial: string[],
    onChange: (value: string[]) => void
  ) => {
    let resultMaterialNames: string[] = [];
    if (selectedMaterial.includes(id)) {
      resultMaterialNames = selectedMaterial.filter((i) => i !== id);
    } else {
      resultMaterialNames = [...selectedMaterial, id];
    }

    onChange(resultMaterialNames);
  };

  const getCollectMaterialName = async (id: string) => {
    if (!id) {
      setMaterials([]);
      return;
    }

    await getCollectRouteMaterialNames({
      id,
      successCallback: (data) => {
        setMaterials(data);
      },
      failedCallback: () => {
        setMaterials([]);
      },
    });
  };

  const handleChangeSiteId = (value: string, changeValue: ChangeValueType) => {
    getCollectDriver(value);
    changeValue('driverIds', []);

    getCollectMaterialName(value);
    changeValue('materials', []);
  };

  useEffect(() => {
    handleChangeSiteId(routeEditing?.siteId || '', () => {});

    if (routeEditing?.id != null) {
      setInitialDriverIds(routeEditing.driverIds || []);
      setInitialMaterials(routeEditing.materials || []);
    }
  }, [routeEditing?.id]);

  useEffect(() => {
    if (isAdminSite && !!collectionSiteOptions.length) {
      handleChangeSiteId(collectionSiteOptions[0].value, () => {});
    }
  }, [isAdminSite, collectionSiteOptions]);

  const formatRouteDataForUpdate = (values: RouteForm): RouteFormUpdate => {
    const currentDriverIds = values.driverIds || [];
    const currentMaterials = values.materials || [];

    return {
      ...values,
      driverIds: {
        connect: currentDriverIds.filter(
          (id) => !initialDriverIds.includes(id)
        ),
        disconnect: initialDriverIds.filter(
          (id) => !currentDriverIds.includes(id)
        ),
      },
      materials: {
        connect: currentMaterials.filter(
          (material) => !initialMaterials.includes(material)
        ),
        disconnect: initialMaterials.filter(
          (material) => !currentMaterials.includes(material)
        ),
      },
    };
  };

  const handleFormSubmit = (values: RouteForm) => {
    if (
      createUpdateModal.type === ModalType.update &&
      routeEditing?.id != null
    ) {
      const formattedValues = formatRouteDataForUpdate(values);
      onSubmit(formattedValues, routeEditing.id);
    } else {
      onSubmit(values);
    }
  };

  return (
    <>
      <div className={styles.createBody}>
        <Form<RouteForm>
          initialValues={initValues}
          onSubmit={handleFormSubmit}
          validate={(values) => {
            return {
              name: values.name?.trim()
                ? validateStringField(
                    t('route_list.thead_route_name'),
                    30,
                    values.name
                  )
                : undefined,
              category: values.category?.trim()
                ? validateStringField(
                    t('route_list.thead_route_category'),
                    100,
                    values.category
                  )
                : undefined,
              siteId: validateSelectField(
                t('route_list.thead_collection_site') || '',
                values.siteId
              ),
              driverIds: validateMultipleSelectField(
                values.driverIds,
                t('route_list.thead_assigned_driver') || ''
              ),
              materials: validateMultipleSelectField(
                values.materials,
                t('route_list.thead_collect_material') || ''
              ),
            };
          }}
          mutators={{
            changeValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
            ...arrayMutators,
          }}
        >
          {(props) => (
            <form className={styles.formInput} autoComplete="off">
              <div className={styles.inputFrame}>
                <div className={styles.inputFramRow2}>
                  <div className={styles.routeName}>
                    <InputDataListField
                      label={t('route_list.thead_route_name') || 'ルート名'}
                      placeholder={
                        t('route_list.tplaceholder_route_name') ||
                        '例：水曜ルートC'
                      }
                      name="name"
                      maxlength={30}
                      isDarkMode
                      autocomplete="off"
                      required={true}
                    />
                  </div>
                  <div className={styles.routeCategory}>
                    <InputDataListField
                      label={
                        t('route_list.thead_route_category') || 'ルートカテゴリ'
                      }
                      placeholder={
                        t('route_list.tplaceholder_route_category') || '例：月'
                      }
                      name="category"
                      maxlength={100}
                      isDarkMode
                      dataSuggestions={routeCategories}
                      onSelectSuggestItem={(value) => {
                        props.form.mutators.changeValue('category', value);
                      }}
                      required={true}
                    />
                  </div>
                </div>

                <SelectField
                  label={t('route_list.thead_collection_site') || '回収拠点'}
                  name="siteId"
                  placeholder={t('common.select') || '選択する'}
                  options={collectionSiteOptions}
                  disabled={!collectionSiteOptions.length || isAdminSite}
                  isDarkmode
                  onChange={(value) => {
                    handleChangeSiteId(
                      value as string,
                      props.form.mutators.changeValue
                    );
                  }}
                  required={true}
                />

                <Field name="driverIds">
                  {({ input, meta }) => (
                    <div className={styles.cardContainer}>
                      <Label
                        text={
                          t('route_list.thead_assigned_driver') ||
                          '担当ドライバー（複数選択可）'
                        }
                        tag={'div'}
                        className={styles.cardLabel}
                        required={true}
                      />
                      <div className="scroll-contents">
                        {drivers?.length > 0 ? (
                          drivers.map((item, i) => {
                            return (
                              <CheckBoxCardV2
                                type="green"
                                key={i}
                                id={`driverIds-${i}`}
                                checked={props.values.driverIds.includes(
                                  item.id
                                )}
                                onChange={() => {
                                  handleChangeDriver(
                                    item.id,
                                    props.values.driverIds,
                                    input.onChange
                                  );
                                }}
                                checkCardCSS={styles.checkBoxCardContainer}
                                childCSS={styles.checkBoxCardChild}
                                enableCardClick={true}
                              >
                                <Popover
                                  content={item.name}
                                  autoGen={{
                                    lines: 1,
                                  }}
                                  positions={['top']}
                                  containerStyle={{
                                    zIndex: '30001',
                                    maxWidth: '600px',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  <p className="name">{item.name}</p>
                                </Popover>
                                <p className="code">ID: {item.id}</p>
                              </CheckBoxCardV2>
                            );
                          })
                        ) : (
                          <div className="no-data">{t('messages.M_008')}</div>
                        )}
                      </div>
                      {meta &&
                        (meta.touched || meta.modified) &&
                        meta.error && (
                          <ErrorText
                            text={meta.error}
                            className={styles.error}
                          />
                        )}
                    </div>
                  )}
                </Field>

                <Field name="materials">
                  {({ input, meta }) => (
                    <div className={styles.cardContainer}>
                      <Label
                        text={
                          t('route_list.thead_collect_material') || '回収材料'
                        }
                        tag={'div'}
                        className={styles.cardLabel}
                        required={true}
                      />
                      <div className="scroll-contents">
                        {materials?.length > 0 ? (
                          <div className={styles.materialContainer}>
                            {materials.map((item, i) => {
                              return (
                                <SelectCollectionMaterialCard
                                  key={i}
                                  id={`materials-${i}`}
                                  material={item}
                                  isSelected={props.values.materials.includes(
                                    item.materialName
                                  )}
                                  onSelectMaterial={() =>
                                    handleChangeMaterial(
                                      item.materialName,
                                      props.values.materials,
                                      input.onChange
                                    )
                                  }
                                ></SelectCollectionMaterialCard>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-data">{t('messages.M_008')}</div>
                        )}
                      </div>
                      {meta &&
                        (meta.touched || meta.modified) &&
                        meta.error && (
                          <ErrorText
                            text={meta.error}
                            className={styles.error}
                          />
                        )}
                    </div>
                  )}
                </Field>
              </div>

              <div className={styles.submit}>
                <button
                  className={`${styles.cancelButton}`}
                  onClick={() => onClose()}
                  type="button"
                >
                  {t('common.button.cancel')}
                </button>
                <button
                  disabled={props.invalid}
                  className={
                    props.invalid ? styles.disableButton : styles.submitButton
                  }
                  onClick={() => props.handleSubmit()}
                  type="button"
                >
                  {createUpdateModal.type === ModalType.add
                    ? t('common.modal_create.add_button')
                    : t('route_list.edit_btn')}
                </button>
              </div>
            </form>
          )}
        </Form>
      </div>
    </>
  );
};

export default CreateUpdateRouteForm;
